import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect, useRef, useState } from "react";
import { pick, useT } from "../i18n/index.js";
import { Panel } from "../ui/Panel.js";
import { Button } from "../ui/Button.js";
import { Timer } from "../ui/Timer.js";
import { useStore } from "./state.js";
import { vipApi } from "./api.js";
import { BundleList, OfferCard } from "./cards.js";
import { AssetIcon } from "../assets/Icon.js";
import { AutoCollectorControl } from "../base/AutoCollectorControl.js";

function unlockText(key: string, t: ReturnType<typeof useT>): string {
  const [head, tail] = key.split(":");
  const k = `store.unlock.${(head ?? key).replace(/[^a-z0-9]/gi, "_")}`;
  const text = t(k, { v: tail ?? "" });
  return text === k ? key : text;
}

/** VIP lounge: level ring, benefits, next unlocks, the MegaVIP ladder, the daily chest and the weekly VIP shop (R55). */
export function VipLounge() {
  const t = useT();
  const claiming = useRef(false);
  const [busy, setBusy] = useState(false);
  const vip = useStore((s) => s.vip);
  const view = useStore((s) => s.view);
  const refreshVip = useStore((s) => s.refreshVip);
  const run = useStore((s) => s.run);
  const celebrate = useStore((s) => s.celebrate);
  useEffect(() => {
    void refreshVip();
  }, [refreshVip]);
  if (!vip) return <p className="hint">{t("common.loading")}</p>;
  const upgrade = view?.packs.find(card => card.key === "pack.vip");
  const nextUnlock = vip.nextUnlocks[0];
  const chest = async () => {
    if (claiming.current || !vip.chest.claimable) return;
    claiming.current = true; setBusy(true);
    try {
      const res = await run(() => vipApi.claimChest(), t("store.vip.chestClaimed"));
      if (res) {
        celebrate({ key: "vip.chest", granted: res.granted, doubled: false, vipPoints: 0, nextStep: null });
        await refreshVip();
      }
    } finally { claiming.current = false; setBusy(false); }
  };
  return (
    <>
      <Panel title={t("store.tab.vip")} className="vip-status-panel">
        <div className="vip-head" data-testid="vip-lounge">
          <AssetIcon id="bld.vip_lounge" size={124} alt="" className="vip-location-art" />
          <div className="vip-ring" style={{ ["--pct" as string]: `${vip.progressPct}%` }}>
            <div className="vip-ring-inner">
              <div className="vip-level">{vip.megaRank > 0 ? `M${vip.megaRank}` : vip.level}</div>
              <div className="dim" style={{ fontSize: 11 }}>
                {vip.megaRank > 0
                  ? t("hud.megavip", { rank: vip.megaRank })
                  : t("hud.vip", { level: vip.level })}
              </div>
            </div>
          </div>
          <div className="grow">
            <div className="mono" data-testid="vip-points">
              {t("store.vip.points", { n: vip.points, next: vip.nextThreshold })}
            </div>
            <div className="progress" style={{ margin: "6px 0" }}>
              <div className="progress-fill gold" style={{ width: `${vip.progressPct}%` }} />
            </div>
            <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
              <span className="badge">
                +{vip.productionPct}% {t("store.vip.production")}
              </span>
              <span className="badge">
                +{vip.buildSpeedPct}% {t("store.vip.build")}
              </span>
              {vip.unitStatPct > 0 ? (
                <span className="badge gold">
                  +{vip.unitStatPct}% {t("store.vip.units")}
                </span>
              ) : null}
              <span className="badge">{t("store.vip.streak", { n: vip.streak })}</span>
            </div>
          </div>
        </div>
        {nextUnlock ? <p className="vip-next-unlock" data-testid="vip-next-unlock"><b>VIP {nextUnlock.level}</b> {nextUnlock.unlocks.map(u => unlockText(u, t)).join(" · ")}</p> : null}
        <section className="vip-daily-reward">
          <AssetIcon id="itm.chest_gold" size={84} alt="" />
          <div><h3>{t("store.vip.chest")}</h3>
          {vip.chest.claimable ? <Button variant="gold" onClick={() => void chest()} disabled={busy} aria-busy={busy} data-testid="vip-chest">{t("common.claim")}</Button> : <span className="mono dim">{vip.level > 0 ? <Timer finishesAt={vip.chest.resetsAt} /> : t("store.vip.chestLocked")}</span>}
          </div>
          <details className="field-details"><summary>{t("store.redesign.contents")}</summary><BundleList bundle={vip.chest.reward} /></details>
        </section>
        <details className="field-details">
          <summary>{t("store.vip.unlocks")}</summary>
          <ul className="perks">
            {vip.unlocks.map((u) => (
              <li key={u}>
                <FunctionalIcon name="check" /> {unlockText(u, t)}
              </li>
            ))}
          </ul>
          {vip.nextUnlocks.map((n) => (
            <div key={n.level} className="dim" style={{ fontSize: 12 }}>
              VIP {n.level}: {n.unlocks.map((u) => unlockText(u, t)).join(" · ")}
            </div>
          ))}
        </details>
        <details className="field-details">
          <summary>{t("store.vip.mega")}</summary>
          <p className="hint">{t("store.vip.megaHint")}</p>
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            {vip.megaLadder.map((m) => (
              <span key={m.rank} className="badge mono">
                M{m.rank} · {m.points.toLocaleString("de-DE")}
              </span>
            ))}
          </div>
        </details>
      </Panel>
      <AutoCollectorControl />
      {upgrade ? <section className="vip-upgrade-panel">
        <header><h2>{pick({ de: "VIP dauerhaft", en: "Permanent VIP" })}</h2><strong data-testid="vip-upgrade-points">+{upgrade.vipPoints + (upgrade.contents.vipPoints ?? 0)} {t("store.vipPoints")}</strong></header>
        <div className="offer-showcase" data-testid="vip-upgrade-offer"><OfferCard card={upgrade} /></div>
        <details className="field-details"><summary>{pick({ de: "VIP-Regeln", en: "VIP rules" })}</summary><p>{pick({ de: "Ein VIP-Punkt je ausgegebenem Credit, dazu die im Paket angezeigten Zusatzpunkte. Dein VIP-Status bleibt dauerhaft. Kein Abo und keine automatische Verlängerung.", en: "One VIP point per Credit spent, plus the bonus points shown in the pack. Your VIP status is permanent. No subscription or automatic renewal." })}</p></details>
      </section> : null}
      <Panel title={t("store.vip.shop")}>
        <p className="hint">{t("store.vip.shopHint")}</p>
        <div className="offers" data-testid="vip-shop">
          {vip.shop.map((c) => (
            <OfferCard key={c.key} card={c} />
          ))}
        </div>
      </Panel>
    </>
  );
}
